/**
 * ElectionFilter - Phoenix Premium Design
 *
 * Cascading dropdown filters for electoral analysis.
 * Brand color: #00468C (Phoenix blue)
 *
 * Based on: docs/design/UI_IMPROVEMENTS_PLAN.md
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { findMunicipality, getPartyHistory, listSenateDistricts, SenateDistrictListItem } from '../../services/api';
import { MunicipalityResult, PartyResult, SenateDistrictInfo } from '../../types';
import NoTranslate from '../common/NoTranslate';

// Election type definitions
const ELECTION_TYPES = [
  { value: 'komunalni', label: 'Komunální volby', icon: '🏛️' },
  { value: 'senatni', label: 'Senátní volby', icon: '🏛️' },
];

interface FilterState {
  electionType: string;
  municipalityCode: string;
  municipalityName: string;
  party: string;
  socialPulse: boolean;
  clientPartyIsNew: boolean;
}

interface ElectionFilterProps {
  onFilterChange: (filters: FilterState) => void;
  onAnalysisRequest: (filters: FilterState, additionalInfo: string) => void;
  isLoading?: boolean;
  additionalInfo: string;
  onAdditionalInfoChange: (value: string) => void;
  resetTrigger?: number;
}

// Icons
const FilterIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
  </svg>
);

const ChevronDownIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);

const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <polygon points="5 3 19 12 5 21 5 3"/>
  </svg>
);

const MapPinIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
    <circle cx="12" cy="10" r="3"/>
  </svg>
);

// Document icon for additional info
const DocumentIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/>
    <polyline points="10 9 9 9 8 9"/>
  </svg>
);

const ElectionFilter: React.FC<ElectionFilterProps> = ({
  onFilterChange,
  onAnalysisRequest,
  isLoading = false,
  additionalInfo,
  onAdditionalInfoChange,
  resetTrigger = 0
}) => {
  // Restore persisted filter state from sessionStorage
  const savedFilter = (() => {
    try {
      const stored = sessionStorage.getItem('phoenix_filter_state');
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  })();

  // Filter state
  const [electionType, setElectionType] = useState<string>(savedFilter?.electionType || 'komunalni');
  const [municipalityQuery, setMunicipalityQuery] = useState<string>(savedFilter?.municipalityName || '');
  const [municipalityCode, setMunicipalityCode] = useState<string>(savedFilter?.municipalityCode || '');
  const [municipalityName, setMunicipalityName] = useState<string>(savedFilter?.municipalityName || '');
  const [party, setParty] = useState<string>(savedFilter?.party || '');
  const [socialPulse, setSocialPulse] = useState<boolean>(false);
  const [partyIsNew, setPartyIsNew] = useState<boolean>(savedFilter?.clientPartyIsNew || false);
  const [newPartyName, setNewPartyName] = useState<string>(savedFilter?.newPartyName || '');

  // Dropdown data
  const [municipalities, setMunicipalities] = useState<MunicipalityResult[]>([]);
  const [parties, setParties] = useState<PartyResult[]>([]);

  // UI state
  const [showMunicipalityDropdown, setShowMunicipalityDropdown] = useState<boolean>(false);
  const [municipalityLoading, setMunicipalityLoading] = useState<boolean>(false);
  const [partyLoading, setPartyLoading] = useState<boolean>(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);

  // Senate district info (for municipality -> district mapping)
  const [senateDistrictInfo, setSenateDistrictInfo] = useState<SenateDistrictInfo | null>(null);
  const [senateDistrictLoading, setSenateDistrictLoading] = useState<boolean>(false);

  // Senate districts list (all 81 districts for dropdown)
  const [senateDistricts, setSenateDistricts] = useState<SenateDistrictListItem[]>([]);
  const [senateDistrictsLoading, setSenateDistrictsLoading] = useState<boolean>(false);

  // Load senate districts when election type changes to senatni
  useEffect(() => {
    const loadSenateDistricts = async () => {
      if (electionType !== 'senatni') {
        return;
      }

      // Only load if we don't have districts yet
      if (senateDistricts.length > 0) {
        return;
      }

      setSenateDistrictsLoading(true);
      try {
        const districts = await listSenateDistricts();
        setSenateDistricts(districts);
      } catch (error) {
        console.error('Failed to load senate districts:', error);
      } finally {
        setSenateDistrictsLoading(false);
      }
    };

    loadSenateDistricts();
  }, [electionType, senateDistricts.length]);

  // Reset dependent fields when election type changes (skip initial mount to preserve restored state)
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setMunicipalityCode('');
    setMunicipalityName('');
    setMunicipalityQuery('');
    setParty('');
    setParties([]);
    setSenateDistrictInfo(null);
    setSocialPulse(false);
    setPartyIsNew(false);
    setNewPartyName('');
  }, [electionType]);

  // Reset form when resetTrigger changes (after successful submission)
  useEffect(() => {
    if (resetTrigger > 0) {
      setElectionType('komunalni');
      setMunicipalityCode('');
      setMunicipalityName('');
      setMunicipalityQuery('');
      setParty('');
      setParties([]);
      setSenateDistrictInfo(null);
      setSocialPulse(false);
      setPartyIsNew(false);
      setNewPartyName('');
      try { sessionStorage.removeItem('phoenix_filter_state'); } catch {}
    }
  }, [resetTrigger]);

  // Debounced municipality search
  useEffect(() => {
    const searchMunicipalities = async () => {
      if (municipalityQuery.length < 2) {
        setMunicipalities([]);
        return;
      }

      setMunicipalityLoading(true);
      try {
        const results = await findMunicipality(municipalityQuery, 30);
        setMunicipalities(results);
        setShowMunicipalityDropdown(true);
      } catch (error) {
        console.error('Municipality search error:', error);
        setMunicipalities([]);
      } finally {
        setMunicipalityLoading(false);
      }
    };

    const debounceTimer = setTimeout(searchMunicipalities, 300);
    return () => clearTimeout(debounceTimer);
  }, [municipalityQuery]);

  // Fetch parties when municipality/district is selected
  useEffect(() => {
    const fetchParties = async () => {
      if (!municipalityCode) {
        setParties([]);
        return;
      }

      setPartyLoading(true);
      try {
        const results = await getPartyHistory(municipalityCode, electionType);
        setParties(results);
        setParty('');
      } catch (error) {
        console.error('Party fetch error:', error);
        setParties([]);
      } finally {
        setPartyLoading(false);
      }
    };

    fetchParties();
  }, [municipalityCode, electionType]);

  // Senate district info - now built from senateDistricts array (no API call needed)
  // The old getSenateDistrictInfo() was for municipality->district mapping,
  // but now we select districts directly from dropdown
  useEffect(() => {
    if (electionType !== 'senatni' || !municipalityCode) {
      setSenateDistrictInfo(null);
      return;
    }

    // Build info from already-loaded senateDistricts array
    const district = senateDistricts.find(d => d.district_id.toString() === municipalityCode);
    if (district) {
      // Create a simplified SenateDistrictInfo from dropdown data
      setSenateDistrictInfo({
        district_id: district.district_id,
        district_name: district.district_name,
        municipality_count: 0, // Not available from list endpoint
        main_municipalities: [],
        all_municipality_codes: [],
        all_municipality_names: [],
        election_year: district.election_year
      });
    } else {
      setSenateDistrictInfo(null);
    }
    setSenateDistrictLoading(false);
  }, [municipalityCode, electionType, senateDistricts]);

  // Notify parent of filter changes
  const notifyFilterChange = useCallback(() => {
    onFilterChange({
      electionType,
      municipalityCode,
      municipalityName,
      party: partyIsNew ? newPartyName.trim() : party,
      socialPulse,
      clientPartyIsNew: partyIsNew,
    });
  }, [electionType, municipalityCode, municipalityName, party, socialPulse, partyIsNew, newPartyName, onFilterChange]);

  useEffect(() => {
    notifyFilterChange();
  }, [notifyFilterChange]);

  // Persist filter state to sessionStorage for navigation survival
  useEffect(() => {
    try {
      sessionStorage.setItem('phoenix_filter_state', JSON.stringify({
        electionType, municipalityCode, municipalityName, party,
        clientPartyIsNew: partyIsNew, newPartyName,
      }));
    } catch {}
  }, [electionType, municipalityCode, municipalityName, party, partyIsNew, newPartyName]);

  // Handle municipality selection (for komunalni)
  const handleMunicipalitySelect = (municipality: MunicipalityResult) => {
    setMunicipalityCode(municipality.code);
    setMunicipalityName(municipality.name);
    setMunicipalityQuery(municipality.name);
    setShowMunicipalityDropdown(false);
  };

  // Handle senate district selection (for senatni)
  const handleSenateDistrictSelect = (districtId: string) => {
    const district = senateDistricts.find(d => d.district_id.toString() === districtId);
    if (district) {
      // Use district_id as the code (backend expects this for senate)
      setMunicipalityCode(districtId);
      setMunicipalityName(`Obvod ${district.district_id} - ${district.district_name}`);
      setMunicipalityQuery(`Obvod ${district.district_id} - ${district.district_name}`);
    } else {
      setMunicipalityCode('');
      setMunicipalityName('');
      setMunicipalityQuery('');
    }
  };

  // Handle analysis request - show confirmation dialog first
  const handleAnalysisRequest = () => {
    if (!municipalityCode) {
      alert('Vyberte prosím obec/okres.');
      return;
    }
    if (partyIsNew ? newPartyName.trim().length < 2 : !party) {
      alert('Vyberte prosím stranu. Strategická analýza vyžaduje výběr strany.');
      return;
    }
    setShowConfirmDialog(true);
  };

  // Confirmed analysis submission
  const handleConfirmedSubmit = () => {
    setShowConfirmDialog(false);
    onAnalysisRequest({
      electionType,
      municipalityCode,
      municipalityName,
      party: partyIsNew ? newPartyName.trim() : party,
      socialPulse,
      clientPartyIsNew: partyIsNew,
    }, additionalInfo);
  };

  // Clear municipality selection
  const clearMunicipality = () => {
    setMunicipalityCode('');
    setMunicipalityName('');
    setMunicipalityQuery('');
    setParty('');
    setParties([]);
    setSenateDistrictInfo(null);
    setSocialPulse(false);
    setPartyIsNew(false);
    setNewPartyName('');
  };

  // Check if form is valid - both municipality and party (or new party name) are required
  const isFormValid = !!municipalityCode && (partyIsNew ? newPartyName.trim().length >= 2 : !!party);

  return (
    <div
      className="rounded-2xl p-6 mb-6"
      style={{
        background: 'var(--bg-secondary)',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
      }}
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ background: 'var(--phoenix-100)', color: 'var(--phoenix-600)' }}
        >
          <FilterIcon />
        </div>
        <div>
          <h2 className="font-display text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
            Filtr volebních dat
          </h2>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            Vyberte typ voleb a obec pro analýzu
          </p>
        </div>
      </div>

      {/* Filter Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Election Type */}
        <div>
          <label
            className="block text-sm font-medium mb-2"
            style={{ color: 'var(--text-primary)' }}
          >
            Typ voleb
          </label>
          <div className="relative">
            <select
              value={electionType}
              onChange={(e) => setElectionType(e.target.value)}
              disabled={isLoading}
              className="input-phoenix appearance-none pr-10 cursor-pointer"
            >
              {ELECTION_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
            <div
              className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
              style={{ color: 'var(--text-tertiary)' }}
            >
              <ChevronDownIcon />
            </div>
          </div>
        </div>

        {/* Municipality Search (komunalni) OR Senate District Dropdown (senatni) */}
        <div className="relative">
          <label
            className="block text-sm font-medium mb-2"
            style={{ color: 'var(--text-primary)' }}
          >
            {electionType === 'senatni' ? 'Senátní obvod' : 'Obec / Okres'}
          </label>

          {electionType === 'senatni' ? (
            /* Senate District Dropdown */
            <div className="relative">
              <select
                value={municipalityCode}
                onChange={(e) => handleSenateDistrictSelect(e.target.value)}
                disabled={isLoading || senateDistrictsLoading}
                className="input-phoenix appearance-none pr-10 cursor-pointer"
              >
                <option value="">Vyberte senátní obvod (1-81)...</option>
                {senateDistricts.map((d) => (
                  <NoTranslate as="option" key={d.district_id} value={d.district_id}>
                    {d.district_id}. {d.district_name}
                  </NoTranslate>
                ))}
              </select>
              <div
                className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
                style={{ color: 'var(--text-tertiary)' }}
              >
                {senateDistrictsLoading ? (
                  <div
                    className="w-4 h-4 border-2 rounded-full animate-spin"
                    style={{
                      borderColor: 'var(--phoenix-200)',
                      borderTopColor: 'var(--phoenix-500)'
                    }}
                  />
                ) : (
                  <ChevronDownIcon />
                )}
              </div>
            </div>
          ) : (
            /* Municipality Search Input */
            <>
              <div className="relative">
                <input
                  type="text"
                  value={municipalityQuery}
                  onChange={(e) => {
                    setMunicipalityQuery(e.target.value);
                    setMunicipalityCode('');
                    setMunicipalityName('');
                  }}
                  onFocus={() => municipalityQuery.length >= 2 && setShowMunicipalityDropdown(true)}
                  placeholder="Začněte psát název..."
                  disabled={isLoading}
                  className="input-phoenix pr-10"
                />
                {municipalityLoading && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div
                      className="w-5 h-5 border-2 rounded-full animate-spin"
                      style={{
                        borderColor: 'var(--phoenix-200)',
                        borderTopColor: 'var(--phoenix-500)'
                      }}
                    />
                  </div>
                )}
                {municipalityCode && !municipalityLoading && (
                  <button
                    onClick={clearMunicipality}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full transition-colors hover:bg-gray-100"
                    style={{ color: 'var(--text-tertiary)' }}
                  >
                    <XIcon />
                  </button>
                )}
              </div>

              {/* Municipality Dropdown */}
              {showMunicipalityDropdown && municipalities.length > 0 && (
                <div
                  className="absolute z-20 w-full mt-2 rounded-xl overflow-y-auto"
                  style={{
                    background: 'var(--bg-secondary)',
                    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
                    border: '1px solid var(--border-light)',
                    maxHeight: '300px'
                  }}
                >
                  {municipalities.map((m) => (
                    <button
                      key={m.code}
                      onClick={() => handleMunicipalitySelect(m)}
                      className="w-full text-left px-4 py-3 transition-colors hover:bg-gray-50 border-b last:border-b-0"
                      style={{ borderColor: 'var(--border-light)' }}
                    >
                      <div className="flex items-start gap-3">
                        <span style={{ color: 'var(--phoenix-500)' }}>
                          <MapPinIcon />
                        </span>
                        <div>
                          <NoTranslate as="div" className="font-medium" style={{ color: 'var(--text-primary)' }}>
                            {m.name}
                          </NoTranslate>
                          <NoTranslate as="div" className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                            {m.district} • Kód: {m.code}
                          </NoTranslate>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Party Dropdown */}
        <div>
          <label
            className="block text-sm font-medium mb-2"
            style={{ color: 'var(--text-primary)' }}
          >
            Strana <span style={{ color: 'var(--danger-600)' }}>*</span>
          </label>
          {!partyIsNew && (
            <div className="relative">
              <select
                value={party}
                onChange={(e) => { setParty(e.target.value); setSocialPulse(false); }}
                disabled={isLoading || parties.length === 0 || partyLoading}
                className="input-phoenix appearance-none pr-10 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">Vyberte stranu...</option>
                {parties.map((p) => (
                  <NoTranslate as="option" key={p.party_id} value={p.party_name}>
                    {p.party_name} ({p.percentage?.toFixed(1)}%)
                  </NoTranslate>
                ))}
              </select>
              <div
                className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
                style={{ color: 'var(--text-tertiary)' }}
              >
                {partyLoading ? (
                  <div
                    className="w-4 h-4 border-2 rounded-full animate-spin"
                    style={{
                      borderColor: 'var(--phoenix-200)',
                      borderTopColor: 'var(--phoenix-500)'
                    }}
                  />
                ) : (
                  <ChevronDownIcon />
                )}
              </div>
            </div>
          )}
          {partyLoading && !partyIsNew && (
            <p className="text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
              Načítám strany...
            </p>
          )}
          <div className="mt-3 flex items-center gap-2">
            <input
              type="checkbox"
              id="party-is-new"
              checked={partyIsNew}
              onChange={(e) => {
                setPartyIsNew(e.target.checked);
                if (e.target.checked) { setParty(''); setSocialPulse(false); }
                else { setNewPartyName(''); }
              }}
              className="rounded"
            />
            <label htmlFor="party-is-new" className="text-sm" style={{ color: 'var(--text-primary)' }}>
              Moje strana v minulých volbách nestavěla — kandidujeme nově
            </label>
          </div>
          {partyIsNew && (
            <div className="mt-2">
              <input
                type="text"
                value={newPartyName}
                onChange={(e) => setNewPartyName(e.target.value)}
                placeholder="Název vaší strany (např. ODS, Sousedé pro Přezletice)"
                minLength={2}
                maxLength={100}
                className="input-phoenix w-full"
              />
              {newPartyName.length > 0 && newPartyName.trim().length < 2 && (
                <p className="text-xs mt-1" style={{ color: 'var(--danger-600)' }}>
                  Zadejte alespoň 2 znaky.
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Info box - Strategy analysis requirements */}
      <div
        className="mt-4 px-4 py-3 rounded-xl"
        style={{ background: 'var(--phoenix-50)', border: '1px solid var(--phoenix-200)' }}
      >
        <div className="flex items-start gap-3">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--phoenix-600)' }}>
            <path d="M9 12l2 2 4-4"/>
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/>
          </svg>
          <div>
            <p className="text-sm font-medium" style={{ color: 'var(--phoenix-700)' }}>
              Strategicka analyza a doporuceni
            </p>
            <ul className="text-xs mt-2 space-y-1.5" style={{ color: 'var(--phoenix-600)' }}>
              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1 h-1 rounded-full flex-shrink-0" style={{ background: 'var(--phoenix-500)' }}></span>
                <span><strong>Výběr strany je povinný</strong> - strategická analýza vždy obsahuje individuální doporučení pro vybranou stranu</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1 h-1 rounded-full flex-shrink-0" style={{ background: 'var(--phoenix-500)' }}></span>
                <span><strong>Novy kandidat?</strong> Vyberte predchoziho kandidata s podobnym profilem (strana, vek, povolani)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1 h-1 rounded-full flex-shrink-0" style={{ background: 'var(--phoenix-500)' }}></span>
                <span><strong>Vice informaci = lepsi analyza:</strong> Vyplnte "Doplnujici informace" nebo nahrajte CV v sekci "Kandidat"</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Info box - cold start warning */}
      <div
        className="mt-4 px-4 py-3 rounded-xl flex items-start gap-3"
        style={{ background: 'var(--warning-50)', border: '1px solid var(--warning-200)' }}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--warning-600)' }}>
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="8" x2="12" y2="12"/>
          <line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
        <div>
          <p className="text-sm font-medium" style={{ color: 'var(--warning-700)' }}>
            Upozorneni na cold start
          </p>
          <p className="text-xs mt-1" style={{ color: 'var(--warning-600)' }}>
            Prvni vyhledavani muze trvat az 30 sekund kvuli startovani serveru. Dalsi vyhledavani jsou okamzita.
          </p>
        </div>
      </div>

      {/* Selected Municipality Badge */}
      {municipalityCode && (
        <div
          className="mt-4 px-4 py-3 rounded-xl flex items-center justify-between"
          style={{ background: 'var(--phoenix-50)', border: '1px solid var(--phoenix-200)' }}
        >
          <div className="flex items-center gap-3">
            <span style={{ color: 'var(--phoenix-500)' }}>
              <MapPinIcon />
            </span>
            <div>
              <NoTranslate as="span" className="font-medium" style={{ color: 'var(--phoenix-700)' }}>
                {municipalityName}
              </NoTranslate>
              <span className="ml-2 text-sm" style={{ color: 'var(--phoenix-500)' }}>
                (kód: {municipalityCode})
              </span>
            </div>
          </div>
          <button
            onClick={clearMunicipality}
            className="p-1.5 rounded-lg transition-colors hover:bg-white/50"
            style={{ color: 'var(--phoenix-500)' }}
          >
            <XIcon />
          </button>
        </div>
      )}

      {/* Senate District Info Box */}
      {electionType === 'senatni' && municipalityCode && senateDistrictInfo && (
        <div
          className="mt-4 px-4 py-4 rounded-xl"
          style={{ background: 'var(--info-50)', border: '1px solid var(--info-200)' }}
        >
          <div className="flex items-center gap-2">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5" style={{ color: 'var(--info-600)' }}>
              <path d="M12 2L2 7l10 5 10-5-10-5z"/>
              <path d="M2 17l10 5 10-5"/>
              <path d="M2 12l10 5 10-5"/>
            </svg>
            <span className="font-semibold" style={{ color: 'var(--info-700)' }}>
              Senátní obvod {senateDistrictInfo.district_id} - <NoTranslate as="span">{senateDistrictInfo.district_name}</NoTranslate>
            </span>
          </div>
          <p className="text-sm mt-2" style={{ color: 'var(--info-600)' }}>
            Analýza pokryje celý senátní obvod. Rok posledních voleb: <strong>{senateDistrictInfo.election_year || 'bude určen automaticky'}</strong>
          </p>
        </div>
      )}

      {/* Additional Info for Deep Research */}
      <div className="mt-6">
        <div className="flex items-center gap-2 mb-2">
          <span style={{ color: 'var(--phoenix-500)' }}>
            <DocumentIcon />
          </span>
          <label
            className="text-sm font-medium"
            style={{ color: 'var(--text-primary)' }}
          >
            Další informace pro analýzu <span style={{ color: 'var(--text-tertiary)' }}>(volitelné)</span>
          </label>
        </div>
        <textarea
          value={additionalInfo}
          onChange={(e) => onAdditionalInfoChange(e.target.value)}
          placeholder="Zde můžete zadat libovolné dodatečné informace, které by měly být zohledněny v hloubkové analýze návrhu kampaně (např. specifika kandidáta, lokální kontext, priority kampaně...)."
          disabled={isLoading}
          rows={4}
          className="input-phoenix w-full resize-none"
          style={{ minHeight: '100px' }}
        />
        <p className="text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
          Tyto informace budou použity v hloubkové analýze pro vytvoření personalizovaného návrhu kampaně.
        </p>
      </div>

      {/* Social Pulse opt-in — visible only when a party is selected */}
      {party && (
        <div className="mt-4 rounded-xl p-4" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)' }}>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={socialPulse}
              onChange={(e) => setSocialPulse(e.target.checked)}
              className="mt-1"
            />
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm" style={{ color: 'var(--text-primary)' }}>
                  Social Pulse <span className="font-normal" style={{ color: 'var(--phoenix-700)' }}>+1 kredit (1 000 Kč)</span>
                </span>
              </div>
              <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                AI analýza veřejných sociálních sítí TOP 5 kandidátů vybrané strany (u senátních voleb 1 kandidát). Součástí politické analýzy a strategie. Pokud se analýza nezdaří, kredit bude automaticky vrácen.
              </p>
            </div>
          </label>
        </div>
      )}

      {/* Action Button */}
      <div className="mt-6 flex justify-end">
        <button
          onClick={handleAnalysisRequest}
          disabled={!isFormValid || isLoading}
          className="btn-phoenix btn-phoenix-primary px-8"
        >
          {isLoading ? (
            <>
              <div
                className="w-5 h-5 border-2 rounded-full animate-spin"
                style={{
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  borderTopColor: 'white'
                }}
              />
              <span>Zpracovávám...</span>
            </>
          ) : (
            <>
              <PlayIcon />
              <span>Spustit analýzu</span>
            </>
          )}
        </button>
      </div>

      {/* Click outside handler */}
      {showMunicipalityDropdown && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => setShowMunicipalityDropdown(false)}
        />
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div
            className="mx-4 w-full max-w-md rounded-2xl p-6 shadow-2xl"
            style={{ background: 'var(--bg-primary)' }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{
                  background: 'linear-gradient(135deg, var(--phoenix-100) 0%, var(--phoenix-200) 100%)',
                  color: 'var(--phoenix-700)'
                }}
              >
                <PlayIcon />
              </div>
              <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
                Potvrdit odeslání analýzy
              </h3>
            </div>

            <div
              className="rounded-xl p-4 mb-4 space-y-2"
              style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)' }}
            >
              <div className="flex justify-between text-sm">
                <span style={{ color: 'var(--text-secondary)' }}>Typ voleb:</span>
                <span className="font-medium" style={{ color: 'var(--text-primary)' }}>
                  {electionType === 'komunalni' ? 'Komunální' : 'Senátní'}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span style={{ color: 'var(--text-secondary)' }}>
                  {electionType === 'komunalni' ? 'Obec:' : 'Obvod:'}
                </span>
                <NoTranslate as="span" className="font-medium text-right" style={{ color: 'var(--text-primary)' }}>
                  {municipalityName}
                </NoTranslate>
              </div>
              {(party || partyIsNew) && (
                <div className="flex justify-between text-sm">
                  <span style={{ color: 'var(--text-secondary)' }}>Strana:</span>
                  <NoTranslate as="span" className="font-medium text-right" style={{ color: 'var(--text-primary)' }}>
                    {partyIsNew ? newPartyName.trim() : party}
                    {partyIsNew && <span className="ml-1 text-xs" style={{ color: 'var(--text-tertiary)' }}>(nová)</span>}
                  </NoTranslate>
                </div>
              )}
              {socialPulse && (
                <div className="flex justify-between text-sm">
                  <span style={{ color: 'var(--text-secondary)' }}>Social Pulse:</span>
                  <span className="font-medium text-right" style={{ color: 'var(--phoenix-700)' }}>
                    Ano (+1 kredit)
                  </span>
                </div>
              )}
              {additionalInfo && (
                <div className="text-sm pt-2" style={{ borderTop: '1px solid var(--border-primary)' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>Doplňující info:</span>
                  <p className="mt-1 text-xs" style={{ color: 'var(--text-primary)' }}>
                    {additionalInfo.length > 100 ? additionalInfo.substring(0, 100) + '...' : additionalInfo}
                  </p>
                </div>
              )}
            </div>

            <p className="text-sm mb-5" style={{ color: 'var(--text-secondary)' }}>
              {socialPulse
                ? 'Opravdu chcete spustit analýzu? Za odeslání budou odečteny 2 kredity (analýza + Social Pulse).'
                : 'Opravdu chcete spustit analýzu? Za odeslání bude odečten 1 kredit.'}
            </p>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowConfirmDialog(false)}
                className="px-5 py-2.5 rounded-xl text-sm font-medium transition-colors"
                style={{
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-primary)'
                }}
              >
                Zrušit
              </button>
              <button
                onClick={handleConfirmedSubmit}
                className="btn-phoenix btn-phoenix-primary px-5"
              >
                <PlayIcon />
                <span>Ano, spustit</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ElectionFilter;
